import {getDistance} from 'geolib';
import {TotalHubriseCalculatorContract} from 'common/api/hubrise/service/TotalHubriseCalculatorContract';
import {HubriseOrderItemContract} from 'common/api/hubrise/model/order/HubriseOrderItemContract';
import {HubriseCurrencyCode} from 'common/api/hubrise/model/common/HubriseCurrencyCode';
import {HubrisePriceUtil} from 'common/api/hubrise/util/HubrisePriceUtil';
import {HubriseOrderOptionContract} from 'common/api/hubrise/model/order/HubriseOrderOptionContract';
import {HubriseOrderContract} from 'common/api/hubrise/model/order/HubriseOrderContract';
import {isNullOrUndefined} from 'common/util/HelperUtil';
import {HubriseMOVSettingContract} from 'common/api/hubrise/model/setting/HubriseMOVSettingContract';
import {HubriseDeliveryFeeSettingContract} from 'common/api/hubrise/model/setting/HubriseDeliveryFeeSettingContract';
import {HubriseLocationContract} from 'common/api/hubrise/model/location/HubriseLocationContract';
import {HubriseServiceType} from 'common/api/hubrise/model/order/HubriseServiceType';

export class TotalHubriseCalculator implements TotalHubriseCalculatorContract {
    public calculateTotals(location: undefined | HubriseLocationContract, order: HubriseOrderContract,
                           deliveryFeeSetting: undefined | HubriseDeliveryFeeSettingContract,
                           movSetting: undefined | HubriseMOVSettingContract, defaultCurrency: HubriseCurrencyCode):
        { subtotal: string, minimumOrderFee?: string, deliveryFee?: string, totalDiscount?: string, total: string } {
        if (!Array.isArray(order.items)) {
            return undefined;
        }

        const currency: HubriseCurrencyCode = this.findCurrency(order.items, defaultCurrency);
        const subtotal: string = HubrisePriceUtil.sum(order.items.map(item => {
            return HubrisePriceUtil.sum([HubrisePriceUtil.multiply(item.price, Number(item.quantity))].concat(
                HubrisePriceUtil.sum((item.options || []).map(option => {
                    return HubrisePriceUtil.multiply(option.price, Number(option.quantity));
                }))
            ));
        }));
        const minimumOrderFee: string = this.calculateMinimumOrderFee(order, subtotal, movSetting, currency);
        const deliveryFee: string = this.calculateDeliveryFee(location, order, deliveryFeeSetting, currency);
        const totalDiscount: string = HubrisePriceUtil.amountToPrice(0, currency);
        const total: string = HubrisePriceUtil.sum([subtotal, minimumOrderFee, deliveryFee, HubrisePriceUtil.multiply(totalDiscount, -1)]);

        return {subtotal, minimumOrderFee, deliveryFee, totalDiscount, total};
    }

    private findCurrency(itemOrOptions: HubriseOrderItemContract[] | HubriseOrderOptionContract[], defaultCurrency: HubriseCurrencyCode): HubriseCurrencyCode {
        return itemOrOptions.reduce((c, i) => {
            const result: { amount: number, currency: HubriseCurrencyCode } = HubrisePriceUtil.priceToAmount(i.price);
            if (typeof result?.currency === 'string' && result.currency !== c) {
                return result.currency;
            }
            if ('options' in i && Array.isArray(i.options)) {
                return this.findCurrency(i.options, c);
            }

            return c;
        }, defaultCurrency);
    }

    private calculateMinimumOrderFee(order: HubriseOrderContract, subtotal: string, movSetting: undefined | HubriseMOVSettingContract, currency: HubriseCurrencyCode): string {
        if (order.service_type !== HubriseServiceType.DELIVERY) {
            return undefined;
        }

        if (!isNullOrUndefined(movSetting)) {
            switch (movSetting.strategy) {
                case 'supplement':
                    if (movSetting.minimum > 0) {
                        if (HubrisePriceUtil.priceToAmount(subtotal).amount < movSetting.minimum) {
                            return HubrisePriceUtil.sum([HubrisePriceUtil.amountToPrice(movSetting.minimum, currency), HubrisePriceUtil.multiply(subtotal, -1)], currency);
                        }

                        return HubrisePriceUtil.amountToPrice(0, currency);
                    }
            }
        }

        return undefined;
    }

    private calculateDeliveryFee(location: undefined | HubriseLocationContract, order: HubriseOrderContract,
                                 deliveryFeeSetting: undefined | HubriseDeliveryFeeSettingContract,
                                 currency: HubriseCurrencyCode): string {
        if (order.service_type !== HubriseServiceType.DELIVERY) {
            return undefined;
        }

        if (!isNullOrUndefined(deliveryFeeSetting) && Array.isArray(deliveryFeeSetting.radiuses)
            && !isNullOrUndefined(location?.custom_fields?.latitude) && !isNullOrUndefined(location?.custom_fields?.longitude)
            && !isNullOrUndefined(order.customer?.latitude) && !isNullOrUndefined(order.customer?.longitude)
        ) {
            switch (deliveryFeeSetting.type) {
                case 'radius': {
                    const distanceInKilometers: number = getDistance(
                        {
                            latitude: Number(location.custom_fields.latitude),
                            longitude: Number(location.custom_fields.longitude)
                        },
                        {
                            latitude: Number(order.customer.latitude),
                            longitude: Number(order.customer.longitude)
                        }) / 1_000;

                    let fee: number = 0;
                    deliveryFeeSetting.radiuses.forEach((radius) => {
                        if (radius.range[0] <= distanceInKilometers && distanceInKilometers < radius.range[1]) {
                            fee = radius.amount
                        }
                    });

                    return HubrisePriceUtil.amountToPrice(fee, currency);
                }
            }
        }

        return HubrisePriceUtil.amountToPrice(0, currency);
    }
}
